import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  HostBinding,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { IFormFieldValidation } from '@nibtravel/shared/util/models';
import { BehaviorSubject, Observable } from 'rxjs';

import { BaseInputComponent } from '../../../common/base-input.component';

/**
 * Component wrapper to facilitate usage accross all form components
 *
 * @export
 * @class ValidationComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'nibtravel-validation',
  template: `
    <ng-container *ngIf="validations">
      <nibtravel-validation-handler
        [validations]="validations"
        [control]="control"
        [host]="host"
        [trigger]="trigger"
        [enableDistinctUntilChanged]="enableDistinctUntilChanged"
        (validationResult)="onValidationResult($event)"
      ></nibtravel-validation-handler>
      <ng-container *ngIf="control.touched">
        <ng-container *ngIf="!errorMessageOnFormGroup">
          <ng-container *ngIf="errors$ | async as errors">
            <nibtravel-error-message
              [errors]="errors"
              *ngIf="errors.length > 0"
            ></nibtravel-error-message>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
})
export class ValidationComponent {
  @HostBinding('class') hostClass = 'validation-host';

  errors$ = new BehaviorSubject<string[]>([]);

  /**
   * Used to manually trigger validations as until FormControl status is changed
   * no events are emitted to trigger checkForErrors.
   */
  @Input() trigger!: Observable<void>;

  /**
   * There are use cases where we need to keep the validation behavior on the field
   * but the message itself to be displayed somewhere else. Use this property combined
   * with the errorMessageComponent
   */
  @Input() errorMessageOnFormGroup = false;

  /** When value is true, the validation will only be triggered if the change value of control is different than the original value. */
  @Input() enableDistinctUntilChanged = true;

  /** The control on which the validations will run against */
  @Input() control!: AbstractControl;

  /** The array of validations applied to the field */
  @Input() validations!: IFormFieldValidation[];

  /**
   * The host element of which the validation will be applied.
   *
   * NOTE: At the moment, there are two usages for this property
   *  1. Style toggling
   *  2. Reference of the host element for the message to allow more
   *     generic messages
   */
  @Input() host!: BaseInputComponent;

  /**
   * The error event, subscribe to the event to receive the error messages
   * Event added to allow message to be rendered in a different position
   * instead of the default implementation. Useful for form groups.
   */
  @Output() errorEmitter = new EventEmitter<string[]>();

  constructor(private cd: ChangeDetectorRef) {}

  get hostElement(): any {
    return this.host?.hostElement?.nativeElement;
  }

  /**
   * Default implementation of onError.
   * It's exclusively to deal with the css toggles
   *
   */
  onValidationResult(errors: string[]): void {
    this.errors$.next(errors);
    this.errorEmitter.emit(errors);
    this.cd.markForCheck();
  }
}
